import { Link } from "react-router-dom";
import '../Styles/HomeStyles.css';
import { useState, useEffect } from "react";
import { 
  ChevronRight, 
  ArrowRight, 
  Mail, 
  X, 
  ShoppingBag, 
  Truck, 
  RefreshCw, 
  Shield, 
  HeadphonesIcon 
} from "lucide-react";

const Home = () => {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);

  // Initialize payment form
  useEffect(() => {
    if (showPaymentForm && window.Square) {
      const initializePaymentForm = async () => {
        try {
          const payments = window.Square.payments("sandbox-sq0idb-YOUR-SANDBOX-APP-ID");
          const card = await payments.card();
          await card.attach("#card-container");
          setPaymentForm(card);
        } catch (e) {
          setErrorMessage("Failed to initialize payment form");
          console.error("Error initializing payment form:", e);
        }
      };

      initializePaymentForm();
    }
  }, [showPaymentForm]);

  // Handle quick buy
  const handleQuickBuy = async (price, productName) => {
    if (!paymentForm) return;
    try {
      const result = await paymentForm.tokenize();
      if (result.status === "OK") {
        console.log(`Processing payment for ${productName}: $${price}`);
        alert("Payment processed successfully!");
        setShowPaymentForm(false);
      }
    } catch (e) {
      setErrorMessage("Payment failed to process");
      console.error("Payment error:", e);
    }
  };

  // Handle newsletter subscription
  const handleSubscribe = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email)) {
      setIsEmailValid(true);
      alert("Thank you for subscribing!");
      setEmail("");
    } else {
      setIsEmailValid(false);
    }
  };

  const featuredProducts = [
    { 
      name: "Summer Floral Dress", 
      price: 89.99, 
      image: "https://i.ibb.co/hWbdFDr/image11.png",
      tag: "New"
    },
    { 
      name: "Classic Denim Jacket", 
      price: 129.99, 
      image: "https://i.ibb.co/v15F6bq/image10.png",
      tag: "Best Seller"
    },
    { 
      name: "Elegant Evening Gown", 
      price: 299.99, 
      image: "https://i.ibb.co/2vs25P1/77.png",
      tag: "Premium"
    },
    { 
      name: "Casual Linen Shirt", 
      price: 59.99, 
      image: "https://i.ibb.co/5TNZGwC/44.png",
      tag: "Sale"
    },
  ];

  const categories = [
    { 
      name: "Summer Collection", 
      count: "42 Items",
      image: "https://i.ibb.co/877SCGs/image4.png"
    },
    { 
      name: "New Arrivals", 
      count: "38 Items",
      image: "https://i.ibb.co/wg59Q4j/image7.png"
    },
    { 
      name: "Best Sellers", 
      count: "56 Items",
      image: "https://i.ibb.co/NZSxyMp/image2.png"
    },
    { 
      name: "Special Offers", 
      count: "24 Items",
      image: "https://i.ibb.co/8mGhpJw/Screenshot-2024-12-05-161520.png"
    },
  ];

  const collections = [
    { 
      name: "Summer Vibes", 
      description: "Light & Breezy Styles",
      image: "https://i.ibb.co/K6q1M6N/image3.png"
    },
    { 
      name: "Evening Elegance", 
      description: "Sophisticated Looks",
      image: "https://i.ibb.co/wJXxX7j/11.png"
    },
    { 
      name: "Casual Comfort", 
      description: "Everyday Essentials",
      image: "https://i.ibb.co/V2h8Mj2/22.png"
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Announcement Bar */}
      <div className="bg-black text-white py-3 px-4 text-center text-sm font-light tracking-wider">
        <div className="flex items-center justify-center space-x-2">
          <Truck className="w-4 h-4" />
          <p>FREE WORLDWIDE SHIPPING ON ORDERS OVER $200</p>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-screen max-h-[600px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" 
             style={{ backgroundImage: 'url(https://i.ibb.co/877SCGs/image4.png)' }}>
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-5xl md:text-7xl font-light mb-6 animate-fadeInDown">
              Summer Collection 2024
            </h1>
            <p className="text-xl md:text-2xl mb-8 font-light animate-fadeInUp">
              Discover the latest trends in fashion
            </p>
            <Link
              to="/products"
              className="group inline-flex items-center bg-white text-black px-12 py-4 text-lg font-light hover:bg-opacity-90 transition-all duration-300 animate-fadeIn"
            >
              SHOP NOW
              <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-light text-center mb-12">TRENDING NOW</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => (
            <div 
              key={category.name} 
              className="relative group overflow-hidden cursor-pointer"
              style={{ 
                animationDelay: `${index * 150}ms`,
                animation: 'fadeInUp 0.5s ease-out forwards'
              }}
            >
              <div className="aspect-w-1 aspect-h-1">
                <img 
                  src={category.image}
                  alt={category.name}
                  className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col items-center justify-center text-white transition-all duration-300">
                  <h3 className="text-2xl font-light mb-2 transform group-hover:-translate-y-2 transition-transform">
                    {category.name}
                  </h3>
                  <p className="text-sm opacity-80">{category.count}</p>
                  <button className="mt-4 px-6 py-2 border border-white hover:bg-white hover:text-black transition-all duration-300 opacity-0 group-hover:opacity-100">
                    Shop Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Featured Products */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-light text-center mb-12">FEATURED PRODUCTS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product, index) => (
              <div 
                key={product.name} 
                className="group relative"
                style={{
                  animationDelay: `${index * 150}ms`,
                  animation: 'fadeInUp 0.5s ease-out forwards'
                }}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 bg-black text-white px-3 py-1 text-sm">
                    {product.tag}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 text-white py-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <button 
                      onClick={() => setShowPaymentForm(true)}
                      className="w-full flex items-center justify-center space-x-2 hover:text-gray-300 transition-colors"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      <span>QUICK BUY</span>
                    </button>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-light text-lg mb-2">{product.name}</h3>
                  <p className="text-gray-600">${product.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Collections */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-light text-center mb-12">OUR COLLECTIONS</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {collections.map((collection, index) => (
            <div 
              key={collection.name} 
              className="relative group overflow-hidden cursor-pointer"
              style={{
                animationDelay: `${index * 150}ms`,
                animation: 'fadeInUp 0.5s ease-out forwards'
              }}
            >
              <img 
                src={collection.image}
                alt={collection.name}
                className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center text-white">
                <h3 className="text-2xl font-light mb-2 transform group-hover:-translate-y-2 transition-transform">
                  {collection.name}
                </h3>
                <p className="text-sm opacity-80 mb-4">{collection.description}</p>
                <Link
                  to="/collection"
                  className="border border-white px-8 py-2 hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center space-x-2"
                >
                  <span>EXPLORE</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300 group">
              <Truck className="w-8 h-8 mb-4 mx-auto transform group-hover:scale-110 transition-transform" />
              <h3 className="font-light mb-2 text-center">FREE SHIPPING</h3>
              <p className="text-gray-400 text-sm text-center">On orders over $200</p>
            </div>
            <div className="p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300 group">
              <RefreshCw className="w-8 h-8 mb-4 mx-auto transform group-hover:scale-110 transition-transform" />
              <h3 className="font-light mb-2 text-center">EASY RETURNS</h3>
              <p className="text-gray-400 text-sm text-center">30-day return policy</p>
            </div>
            <div className="p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300 group">
              <Shield className="w-8 h-8 mb-4 mx-auto transform group-hover:scale-110 transition-transform" />
              <h3 className="font-light mb-2 text-center">SECURE PAYMENT</h3>
              <p className="text-gray-400 text-sm text-center">100% secure checkout</p>
            </div>
            <div className="p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300 group">
              <HeadphonesIcon className="w-8 h-8 mb-4 mx-auto transform group-hover:scale-110 transition-transform" />
              <h3 className="font-light mb-2 text-center">CUSTOMER SUPPORT</h3>
              <p className="text-gray-400 text-sm text-center">24/7 dedicated support</p>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter */}
      <div className="bg-neutral-100 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-light mb-6">JOIN OUR NEWSLETTER</h2>
          <p className="mb-8 text-gray-600 max-w-2xl mx-auto">
            Subscribe to our newsletter and get 10% off your first purchase
          </p>
          <form 
            onSubmit={handleSubscribe}
            className="max-w-md mx-auto flex flex-col sm:flex-row gap-4"
          >
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className={`w-full px-12 py-3 border ${
                  isEmailValid ? 'border-gray-300 focus:border-black' : 'border-red-500'
                } focus:outline-none transition-colors`}
              />
            </div>
            <button 
              type="submit"
              className="bg-black text-white px-8 py-3 hover:bg-gray-900 transition-all duration-300 flex items-center justify-center space-x-2 hover:scale-105"
            >
              <span>SUBSCRIBE</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          {!isEmailValid && (
            <p className="text-red-500 text-sm mt-2">Please enter a valid email address</p>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowPaymentForm(false)}
          ></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full relative transform transition-all duration-300 scale-100 opacity-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-light">QUICK CHECKOUT</h3>
              <button
                onClick={() => setShowPaymentForm(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 rounded">
                {errorMessage}
              </div>
            )}

            <div id="card-container" className="mb-6 min-h-[100px] border p-4 rounded"></div>
            
            <button
              onClick={() => handleQuickBuy(89.99, "Summer Floral Dress")}
              className="w-full bg-black text-white py-3 hover:bg-gray-900 transition-colors rounded flex items-center justify-center space-x-2"
            >
              <span>COMPLETE PURCHASE</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* CSS Keyframes */}
      
    </div>
  );
};

export default Home;